import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";

/**
 * Summarize Collection Threads
 *
 * Ranks every open collection thread in the org by urgency (reusing the same
 * scoring as the AR Summary), asks an LLM for an executive summary + a
 * recommended action per most-pressing thread + an overview of the rest, and
 * renders that into one HTML digest email.
 *
 * Outputs `recipientEmails` (the configured Team's member emails) as an array
 * for fan-out, plus the shared `subject` / `htmlContent` as scalars. Wire
 * `recipientEmails` to a single-string input downstream (Prepare Summary Email)
 * to send one individual email per member; `subject`/`htmlContent` broadcast to
 * each fan-out branch.
 */
export const summarizeCollectionThreadsStep = defineStepConfig({
  type: "summarize_collection_threads",
  name: "Summarize Collection Threads",
  description:
    "Rank open collection threads, generate an LLM executive summary + " +
    "per-thread recommendations for the most pressing, and render a digest " +
    "email for each member of the selected Team.",
  category: "Data Access",
  libraryModule: "AccountsReceivable",

  // No data inputs — just the implicit flow signal from the schedule trigger.
  inputSchema: z.object({}),

  outputSchema: z.object({
    recipientEmails: z
      .array(z.string())
      .describe(
        "Email address of each Team member. Connect to a single-string input " +
          "(Prepare Summary Email) to fan out one email per member.",
      ),
    recipientUserIds: z
      .array(z.string().uuid())
      .describe(
        "User IDs of all Team members. Wire directly to Send Announcement " +
          "(v2) 'recipientUserIds' (with channels=in_app) for an in-app bell " +
          "notification to the whole team — no fan-out needed.",
      ),
    subject: z
      .string()
      .describe("Shared digest subject line / announcement title."),
    htmlContent: z
      .string()
      .describe("Shared rendered HTML digest body (email)."),
    bodyText: z
      .string()
      .describe(
        "Plain-text digest body, for an in-app announcement (Send " +
          "Announcement v2 'body').",
      ),
    threadCount: z
      .number()
      .int()
      .nonnegative()
      .describe("Open threads included in the summary."),
    recipientCount: z
      .number()
      .int()
      .nonnegative()
      .describe("Team members the digest will be sent to."),
  }),

  configSchema: z.object({
    userGroupId: z
      .string()
      .uuid()
      .describe("The Team whose members receive the summary email."),
    topPressingCount: z
      .number()
      .int()
      .positive()
      .default(5)
      .describe(
        "How many of the most urgent threads get an individual LLM " +
          "recommendation in the digest.",
      ),
    emailPreferenceKey: z
      .string()
      .optional()
      .describe(
        "Optional User.notifications key gating the EMAIL recipients: a member " +
          "is dropped from recipientEmails when their notifications[key] === " +
          "false. Use the key your notification-preferences UI writes (e.g. " +
          "send_collection_summary_notification_email). Blank = email everyone.",
      ),
    inAppPreferenceKey: z
      .string()
      .optional()
      .describe(
        "Optional User.notifications key gating the IN-APP recipients " +
          "(recipientUserIds), same semantics as emailPreferenceKey. Blank = " +
          "notify everyone in-app.",
      ),
  }),

  emits: [] as const,

  execution: {
    // LLM call + ranking queries; give it room.
    startToCloseTimeout: "120 seconds",
    retry: {
      maximumAttempts: 3,
      backoffCoefficient: 2,
      initialInterval: "5 seconds",
    },
  },
});
